import { useNavigate } from "@tanstack/react-router";
import { useTransition, useEffect } from "react";

interface UseTableNavigationProps {
  basePath: string;
  currentPage: number;
  nextPageUrl: string | null;
  previousPageUrl: string | null;
  resetFiltersUrl: string;
}

export function useTableNavigation({
  basePath,
  currentPage,
  nextPageUrl,
  previousPageUrl,
  resetFiltersUrl,
}: UseTableNavigationProps) {
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();

  // Prefetch optimizado para evitar conflictos con server preload
  useEffect(() => {
    // Debounce prefetch para evitar ejecuciones excesivas
    const prefetchTimeout = setTimeout(() => {
      // Nota: TanStack Router no tiene prefetch como Next.js
      // Podemos precargar datos de la siguiente página usando otras estrategias
      // como preloading de queries si es necesario
    }, 150); // Delay para permitir que navegaciones rápidas se completen

    return () => clearTimeout(prefetchTimeout);
  }, [nextPageUrl, previousPageUrl]);

  // Función optimizada para navegación de páginas con transición
  const navigateToPage = (page: number) => {
    startTransition(() => {
      if (page === currentPage + 1 && nextPageUrl) {
        navigate({ to: nextPageUrl });
      } else if (page === currentPage - 1 && previousPageUrl) {
        navigate({ to: previousPageUrl });
      } else {
        // Para otras páginas, usar search params
        navigate({
          to: basePath,
          search: (prev: Record<string, unknown>) => {
            // Crear nuevo objeto de search params sin el parámetro page existente
            const newSearch = { ...prev };
            // Asegurarnos de que page sea el nuevo valor
            return { ...newSearch, page };
          },
        });
      }
    });
  };

  // Función para actualizar filtros con transición
  const updateFilters = (filters: Record<string, any>) => {
    startTransition(() => {
      navigate({
        to: basePath,
        search: (prev: Record<string, unknown>) => {
          // Crear nuevo objeto de search params
          const newSearch = { ...prev };

          // Filtrar valores "all" - no enviar parámetro cuando es "all"
          const filteredFilters = Object.fromEntries(
            Object.entries(filters).filter(([_, value]) => value !== "all"),
          );

          // Aplicar filtros y resetear página
          return { ...newSearch, ...filteredFilters, page: 1 };
        },
      });
    });
  };

  // Función para resetear filtros con transición
  const resetFilters = () => {
    startTransition(() => {
      navigate({ to: resetFiltersUrl });
    });
  };

  return {
    navigateToPage,
    updateFilters,
    resetFilters,
    isPending, // Exponer estado de carga para UI
  };
}
